import type { PricePoint } from '@chicare/shared';
import { formatKobo } from '@chicare/shared';

import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';

/**
 * Price history.
 *
 * Nigerian drug prices move, sometimes sharply, and a buyer who has watched a
 * pack go from ₦620 to ₦450 trusts the ₦450 in a way they would not trust it in
 * isolation. That is the entire reason this chart exists — it is a trust
 * component wearing a chart's clothes, which is why it states the change in
 * words as well as drawing it.
 *
 * Drawn as inline SVG rather than a charting library: it is one polyline, it
 * has to render inside a 300px column on a metered connection, and shipping
 * 40KB of chart runtime for it would be absurd.
 */

export interface PriceHistoryProps {
  points: PricePoint[];
  className?: string;
}

export function PriceHistory({ points, className }: PriceHistoryProps) {
  if (points.length < 2) return null;

  const values = points.map((point) => point.price_kobo);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(1, max - min);

  const width = 560;
  const height = 100;
  const step = width / (points.length - 1);

  const coordinates = points.map((point, index) => {
    const x = index * step;
    // Padded 12px top and bottom so the line never touches the frame.
    const y = height - 12 - ((point.price_kobo - min) / span) * (height - 24);

    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  const first = points[0]!;
  const last = points[points.length - 1]!;
  const changeBps =
    first.price_kobo > 0
      ? Math.round(((last.price_kobo - first.price_kobo) / first.price_kobo) * 10_000)
      : 0;

  const fell = changeBps < 0;
  const changePercent = Math.abs(changeBps / 100).toFixed(0);

  return (
    <Card className={className}>
      <div className="mb-3 flex items-baseline justify-between gap-2.5">
        <h2 className="m-0 font-display text-sm font-bold tracking-[-0.01em]">
          Price history · last {points.length} months
        </h2>
        {changeBps !== 0 ? (
          <Badge tone={fell ? 'verified' : 'caution'}>
            {fell ? '↓' : '↑'} {changePercent}%
          </Badge>
        ) : null}
      </div>

      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-24 w-full"
        role="img"
        aria-label={`Lowest price moved from ${formatKobo(first.price_kobo)} in ${first.month} to ${formatKobo(last.price_kobo)} in ${last.month}.`}
      >
        <g stroke="var(--color-line-2)" strokeWidth="1">
          <path d={`M0 20H${width}M0 50H${width}M0 80H${width}`} />
        </g>
        <polyline
          points={coordinates.join(' ')}
          fill="none"
          stroke="var(--color-blue)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <polyline
          points={`${coordinates.join(' ')} ${width},${height} 0,${height}`}
          fill="var(--color-blue)"
          opacity="0.07"
          stroke="none"
        />
        <circle
          cx={width}
          cy={coordinates[coordinates.length - 1]!.split(',')[1]}
          r="5"
          fill="var(--color-blue)"
        />
      </svg>

      <div className="flex justify-between font-mono text-[11px] text-faint tabular">
        <span>
          {formatKobo(first.price_kobo)} · {monthLabel(first.month)}
        </span>
        <span className="font-bold text-blue">
          {formatKobo(last.price_kobo)} · now
        </span>
      </div>
    </Card>
  );
}

function monthLabel(month: string): string {
  const [year, monthNumber] = month.split('-');

  if (!year || !monthNumber) return month;

  return new Date(Number(year), Number(monthNumber) - 1, 1).toLocaleDateString('en-NG', {
    month: 'short',
  });
}
